import api from "../../../utils/api";

// ---------------------------------------------------------------------------
// Cliente de la API del Radar de Correo (`/mailIntel/*`). Las respuestas
// vienen como `{ meta, data }`: se devuelve `data`. La compañía viaja en el
// header x-company-id (lo pone el interceptor de `utils/api`).
// ---------------------------------------------------------------------------

export type Provider = "gmail" | "outlook" | "imap";
export type ConnectionStatus = "pending" | "active" | "paused" | "error" | "revoked" | "budget_exceeded" | "consent_required";
export type OperationStatus = "proposed" | "confirmed" | "dismissed" | "merged";
export type OperationStage = "quote" | "booked" | "in_transit" | "delivered" | "invoiced" | "paid" | "closed";
export type AlertStatus = "open" | "ack" | "resolved" | "snoozed";
export type Severity = "low" | "medium" | "high" | "critical";
export type CounterpartyRole = "carrier" | "forwarder" | "customs_broker" | "supplier" | "customer" | "other";
export type Money = Record<string, number>;

export interface TermsInfo {
  version: string;
  terms: Record<"es" | "en", { title: string; points: string[] }>;
  backfillWindows: number[];
  defaultBackfillMonths: number;
  providers: { gmail: boolean; outlook: boolean; imap: boolean; ai: boolean; enabled: boolean };
}

export interface Connection {
  id: string;
  provider: Provider;
  address: string | null;
  status: ConnectionStatus;
  ownerUserId: string;
  ownerName: string | null;
  mine: boolean;
  consent: { version: string; acceptedAt: string | null; backfillMonths: number } | null;
  consentCurrent: boolean;
  backfill: { since: number | null; done: boolean } | null;
  budget: { capUsd: number; spentUsd: number } | null;
  imap?: { host: string; port: number; user?: string };
  lastSyncAt: unknown;
  lastError: { code: string; message?: string } | null;
  revokeHint: string | null;
}

export interface Job {
  id: string;
  kind: "backfill" | "incremental" | "purge" | "snapshot";
  status: string;
  stage: string;
  counters: Record<string, number>;
  error: string | null;
}

export interface OperationRefs {
  container?: string[];
  awb?: string[];
  bl?: string[];
  booking?: string[];
  pedimento?: string[];
  cfdi?: string[];
  tracking?: string[];
}

export interface Operation {
  id: string;
  status: OperationStatus;
  stage: OperationStage;
  mode: string;
  lane: { origin: string | null; destination: string | null } | null;
  refs: OperationRefs;
  counterparties?: { id: string; name?: string; roles?: CounterpartyRole[] }[];
  counterpartyIds: string[];
  firstEtaAt: number | null;
  etaAt: number | null;
  deliveredAt: number | null;
  firstEventAt: number | null;
  lastEventAt: number | null;
  value: { amount: number; currency: string; kind: string }[];
  confidence: "high" | "medium" | "low";
  conflict: boolean;
  shipmentId: string | null;
  suggestedShipmentId: string | null;
}

export interface OpEvent {
  id: string;
  type: string;
  occurredAt: number;
  counterpartyId: string | null;
  direction: "in" | "out";
  confidence: string;
  data: { etaOld: number | null; etaNew: number | null; documentType: string | null };
}

export interface FinanceItem {
  id: string;
  operationId: string | null;
  counterpartyId: string | null;
  kind: "quote" | "invoice" | "payment" | "credit_note";
  direction: "payable" | "receivable";
  amount: number;
  currency: string;
  issuedAt: number;
  dueDate: number | null;
  status: string;
  paidAt?: number | null;
  cfdiUuid?: string;
  source: "cfdi" | "ai";
}

export interface Counterparty {
  id: string;
  key: string;
  displayName: string;
  roles: CounterpartyRole[];
  roleSource?: "ai" | "user";
  firstSeen: number;
  lastSeen: number;
}

export interface OperationDetail {
  operation: Operation;
  events: OpEvent[];
  finance: FinanceItem[];
  counterparties: Counterparty[];
  messages: { id: string; date: number; direction: string; fromDomain: string; ownerUserId: string }[];
}

export interface Evidence {
  id: string;
  date: number;
  direction: string;
  subject: string;
  excerpt: string;
  note: string | null;
  attachmentNames: string[];
  fromName: string | null;
}

export interface Alert {
  id: string;
  ruleId: string;
  severity: Severity;
  title: string;
  description: string;
  url: string;
  operationId: string | null;
  status: AlertStatus;
  firstAt: number;
  lastAt: number;
  count: number;
}

export interface CounterpartyMetrics {
  counterpartyId: string;
  name: string;
  roles: CounterpartyRole[];
  operations: number;
  quotesReceived: number;
  quotesWon: number;
  quoteWinRate: number | null;
  value: Money;
  delays: number;
  avgDelayHours: number | null;
  incidents: number;
  customsHolds: number;
  incidentRate: number | null;
  problemRate: number | null;
  onTimeRate: number | null;
  responseMedianHours: number | null;
  responseP75Hours: number | null;
  ourResponseMedianHours: number | null;
  avgPaymentDays: number | null;
  open: Money;
  overdue: Money;
  score: number;
}

export interface NarrativeBlock {
  summary: string;
  findings: string[];
  risks: string[];
  recommendations: string[];
}

export interface Snapshot {
  id: string;
  generatedAt: number;
  periodDays: number;
  metrics: {
    totals: {
      operations: number;
      confirmed: number;
      proposed: number;
      counterparties: number;
      carriers: number;
      byStage: Record<string, number>;
      value: Money;
      receivableOpen: Money;
      receivableOverdue: Money;
      payableOpen: Money;
      payableOverdue: Money;
      delays: number;
      incidents: number;
    };
    counterparties: CounterpartyMetrics[];
    rankings: Record<"bestCarriers" | "mostBusiness" | "mostValue" | "fastestResponse" | "slowestResponse" | "mostProblems" | "mostOverdue", string[]>;
    trend: { month: string; operations: number }[];
    modes: Record<string, number>;
    lanes: { origin: string; destination: string; operations: number }[];
  };
  narrative: { es: NarrativeBlock; en: NarrativeBlock } | null;
  narrativeError: string | null;
}

export interface Settings {
  ownDomains: string[];
  ignoredDomains: string[];
  carrierDomains: string[];
  alertRecipientIds: string[];
  scoreWeights: Record<string, number> | null;
  canEdit?: boolean;
}

export interface ConsentInput {
  acceptedTerms: boolean;
  termsVersion: string;
  backfillMonths: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const data = <T>(res: any): T => res?.data?.data as T;

/** Mensaje legible de un error de la API (o `null`). */
export function apiErrorMessage(err: unknown): string | null {
  const r = (err as { response?: { data?: { meta?: { message?: string } } } })?.response;
  return r?.data?.meta?.message || null;
}

const enc = encodeURIComponent;

export const radarCorreoApi = {
  status: async () => data<TermsInfo>(await api.get("/mailIntel/status")),
  connections: async () => data<Connection[]>(await api.get("/mailIntel/connections")),
  startOAuth: async (provider: "gmail" | "outlook", consent: ConsentInput) =>
    data<{ connectionId: string; url: string }>(await api.post("/mailIntel/connections/oauth/start", { provider, ...consent })),
  connectImap: async (input: { host: string; port: number; user: string; password: string } & ConsentInput) =>
    data<Connection>(await api.post("/mailIntel/connections/imap", input)),
  pause: async (id: string) => data<Connection>(await api.post(`/mailIntel/connections/${enc(id)}/pause`)),
  resume: async (id: string) => data<Connection>(await api.post(`/mailIntel/connections/${enc(id)}/resume`)),
  reconsent: async (id: string, consent: ConsentInput) => data<Connection>(await api.post(`/mailIntel/connections/${enc(id)}/reconsent`, consent)),
  disconnect: async (id: string, deleteDerived: boolean) =>
    data<{ id: string; revokeHint: string | null }>(await api.post(`/mailIntel/connections/${enc(id)}/disconnect`, { deleteDerived })),
  syncNow: async (id: string) => data<{ jobId: string }>(await api.post(`/mailIntel/connections/${enc(id)}/sync`)),
  jobs: async (id: string) => data<Job[]>(await api.get(`/mailIntel/connections/${enc(id)}/jobs`)),

  operations: async (params: { status?: string; stage?: string } = {}) => data<Operation[]>(await api.get("/mailIntel/operations", { params })),
  operation: async (id: string) => data<OperationDetail>(await api.get(`/mailIntel/operations/${enc(id)}`)),
  evidence: async (id: string) => data<Evidence[]>(await api.get(`/mailIntel/operations/${enc(id)}/evidence`)),
  confirm: async (id: string) => data(await api.post(`/mailIntel/operations/${enc(id)}/confirm`)),
  dismiss: async (id: string) => data(await api.post(`/mailIntel/operations/${enc(id)}/dismiss`)),
  merge: async (id: string, operationIds: string[]) => data(await api.post(`/mailIntel/operations/${enc(id)}/merge`, { operationIds })),
  linkShipment: async (id: string, shipmentId: string) => data(await api.post(`/mailIntel/operations/${enc(id)}/link-shipment`, { shipmentId })),
  unlinkShipment: async (id: string) => data(await api.post(`/mailIntel/operations/${enc(id)}/unlink-shipment`)),

  counterparties: async () => data<Counterparty[]>(await api.get("/mailIntel/counterparties")),
  updateCounterparty: async (id: string, input: { displayName?: string; roles?: CounterpartyRole[] }) =>
    data<Counterparty>(await api.put(`/mailIntel/counterparties/${enc(id)}`, input)),
  alerts: async (status?: AlertStatus) => data<Alert[]>(await api.get("/mailIntel/alerts", { params: status ? { status } : {} })),
  updateAlert: async (id: string, status: AlertStatus, snoozeDays?: number) => data<Alert>(await api.put(`/mailIntel/alerts/${enc(id)}`, { status, snoozeDays })),
  finance: async () => data<FinanceItem[]>(await api.get("/mailIntel/finance")),

  snapshot: async () => data<Snapshot | null>(await api.get("/mailIntel/snapshot")),
  regenerateSnapshot: async () => data<{ jobId: string }>(await api.post("/mailIntel/snapshot")),
  settings: async () => data<Settings>(await api.get("/mailIntel/settings")),
  saveSettings: async (input: Partial<Settings>) => data<Settings>(await api.put("/mailIntel/settings", input)),
};
